import classNames from 'classnames/bind';
import getCanTableLoadMore from 'mini-mail-list-actions/lib/utils/tables/getCanTableLoadMore';
import getDatapointStatus from 'owa-analytics/lib/utils/getDatapointStatus';
import getIsInCheckedMode from 'mini-mail-list-store/lib/utils/getIsInCheckedMode';
import getListItemHeight, { RowComponentsID } from '../../utils/mailList/getListItemHeight';
import getSelectedTableView from 'mini-mail-list-store/lib/utils/getSelectedTableView';
import loadMoreInTable from 'mini-mail-list-actions/lib/actions/tables/loadMoreInTable';
import type NativeAdUserFlight from 'mini-mail-nativead-userchecker/lib/NativeAdUserFlight';
import React from 'react';
import reloadTable from 'mini-mail-list-actions/lib/actions/tables/reloadTable';
import shouldHideAd from 'mini-mail-nativead-store/lib/utils/shouldHideAd';
import shouldShowAutomaticRepliesBanner from '../../utils/mailList/shouldShowAutomaticRepliesBanner';
import shouldShowMailListItem from '../../utils/mailList/shouldShowMailListItem';
import shouldShowStickyFocusedRollup from '../../utils/mailList/shouldShowStickyFocusedRollup';
import styles from './VirtualizedMailList.scss';
import type TableView from 'mini-mail-list-store/lib/store/schema/TableView';
import VirtualizedList from 'mini-virtualized-list/lib/components/VirtualizedList';
import VirtualizedMailListItem from './VirtualizedMailListItem';
import { ACTION_SELECT_FILTER } from 'mini-mail-filter-actions/lib/utils/actionNames';
import { canShowAdsComponentInListView } from '../helpers/canShowAdsComponentInListView';
import { canShowOfflineSyncInProgress } from '../helpers/canShowOfflineSyncProgress';
import { canShowOnboardingCards } from '../../utils/mailList/canShowOnboardingCards';
import { canShowUpNext } from '../../utils/mailList/canShowUpNext';
import { canShowUSQWarningCard } from '../../utils/mailList/canShowUSQWarningCard';
import { getAdIndexName } from 'mini-mail-nativead-store/lib/utils/getAdIndexName';
import { getAdPlacementIndex } from '../helpers/getAdPlacementIndex';
import { getDisplayCustomisationsContext } from './DisplayCustomisationsProvider';
import { getIsShowMultipleAds } from 'mini-mail-nativead-store/lib/utils/getIsShowMultipleAds';
import { getMailListContainerHeight } from '../helpers/getMailListContainerHeight';
import { getRTL } from '@uifabric/utilities';
import { isInSearchView } from 'mini-mail-list-store/lib/selectors/isInSearchView';
import { isIOS } from 'mini-device-utils/lib/utils/isIOS';
import { isUnifiedSearchView } from 'mini-mail-list-store/lib/selectors/isUnifiedSearchView';
import type { ItemRendererProp } from 'mini-virtualized-list/lib/schema/VirtualizedListProps';
import { type NativeAdPlacement } from 'mini-mail-nativead-store';
import { observer } from 'mobx-react';
import { PerformanceDatapoint } from 'owa-analytics/lib/datapoints/PerformanceDatapoint';
import { registerWorkflowActions } from 'mini-mail-workflow-tracking/lib/utils/actionToSignalMapping';
import { resetGlobalAdState } from 'mini-mail-nativead-view/lib/utils/getGlobalAdState';
import { shouldShowListHeader } from '../helpers/shouldShowListHeader';
import {
    ACTION_LOAD_MORE_IN_TABLE,
    ACTION_ON_LOAD_MORE_ITEMS_SUCCESS,
    ACTION_ON_LOAD_MORE_CONVERSATIONS_SUCCESS,
    ACTION_SELECT_FOCUSED_PIVOT,
} from 'mini-mail-list-actions/lib/utils/actionNames';
import {
    LOAD_MORE_MAIL_STARTED,
    LOAD_MORE_MAIL_ENDED,
    FOCUSED_OTHER_TAB_CLICKED,
    FILTER_SELECTED,
} from 'mini-mail-workflow-tracking/lib/constants/ListViewWorkflowConstants';

interface VirtualizedMailListProps {
    showNativeAdFormat: NativeAdUserFlight;
    onLoadMoreItems?: () => Promise<void>;
    forwardedRefs?: {
        switchRef: React.RefObject<HTMLDivElement>;
        filterRef: React.RefObject<HTMLDivElement>;
    };
    enableSwipeRefresh?: boolean;
}
const DATAPOINT_TIMEOUT = 3 * 60 * 1000; // 3 mins in milliseconds
@observer
export default class VirtualizedMailList extends React.Component<VirtualizedMailListProps, {}> {
    static contextType = getDisplayCustomisationsContext();
    // Class variable to avoid recomputation.
    private virtualListItemIds = null;
    private lastDataSourceId = null;
    private itemHeightsInPx = null;
    private forwardedRefs;

    constructor(props: VirtualizedMailListProps) {
        super(props);
        // Refresh Ad when List Mounts
        this.resetAdInstanceState();
        this.forwardedRefs = props.forwardedRefs;
    }

    componentDidUpdate(prevProps: VirtualizedMailListProps) {
        // Updating lastDataSourceId which will be needed for this.isDataSourceSwitching()
        this.lastDataSourceId = getSelectedTableView().id;

        if (!prevProps.showNativeAdFormat && this.props.showNativeAdFormat) {
            /**
             * It is added just to have same telemetry number for ads as
             * compared to the list without virtualization. This will handle scenario where
             * user switch from non-ads showing folder to Inbox.
             */
            resetGlobalAdState();
        }
    }

    componentDidMount() {
        // Updating lastDataSourceId which will be needed for this.isDataSourceSwitching()
        const selectedTableView = getSelectedTableView();
        this.lastDataSourceId = selectedTableView.id;

        // register all possible action-to-signal mappings that we're interested in,
        // which may get triggered in future by user actions.
        registerWorkflowActions({
            [ACTION_LOAD_MORE_IN_TABLE]: LOAD_MORE_MAIL_STARTED,
            [ACTION_ON_LOAD_MORE_ITEMS_SUCCESS]: LOAD_MORE_MAIL_ENDED,
            [ACTION_ON_LOAD_MORE_CONVERSATIONS_SUCCESS]: LOAD_MORE_MAIL_ENDED,
            [ACTION_SELECT_FOCUSED_PIVOT]: FOCUSED_OTHER_TAB_CLICKED,
            [ACTION_SELECT_FILTER]: FILTER_SELECTED,
        });
    }

    /**
     * This function ensure nativeAd will refresh when VirtualMailList
     * Mount/UnMount or folder is switched.
     */
    private resetAdInstanceState = () => {
        this.props.showNativeAdFormat !== 0 && resetGlobalAdState();
    };

    private getVirtualListItemIds = (): string[] => {
        const tableView = getSelectedTableView();
        const isInMultiSelectMode = getIsInCheckedMode();
        const itemList = tableView.itemIds.filter(this.doesTableConversationExistForItem);
        const isShowMultipleAd = getIsShowMultipleAds();
        const [firstIndex, secondIndex] = getAdIndexName();
        if (
            canShowAdsComponentInListView(isInMultiSelectMode) &&
            this.props.showNativeAdFormat === 1 &&
            RowComponentsID.AdContainerExp
        ) {
            if (isShowMultipleAd && !shouldHideAd(secondIndex, 1)) {
                itemList.splice(
                    0,
                    0,
                    `${RowComponentsID.AdContainerExp} ${secondIndex}` // Standard Native Ad container UX
                );
            }
            if (!shouldHideAd(firstIndex, isShowMultipleAd ? 2 : 1)) {
                itemList.splice(
                    getAdPlacementIndex(),
                    0,
                    `${RowComponentsID.AdContainerExp} ${firstIndex}` // Standard Native Ad container UX
                );
            }
        }

        // Note: Order is important
        return [
            shouldShowListHeader() && RowComponentsID.ListHeader,
            canShowOfflineSyncInProgress() && RowComponentsID.OfflineSyncInProgress,
            canShowAdsComponentInListView(isInMultiSelectMode) &&
                this.props.showNativeAdFormat === 2 &&
                `${RowComponentsID.AdBannerContainer} ${'MiniHeader'}`,
            !isInMultiSelectMode &&
                shouldShowAutomaticRepliesBanner(tableView) &&
                RowComponentsID.AutomaticReplyBanner,
            !isInMultiSelectMode &&
                shouldShowStickyFocusedRollup(tableView) &&
                RowComponentsID.FocusedInboxRollup,
            canShowOnboardingCards() && RowComponentsID.OnboardingCardsCarousel,
            canShowUpNext() && RowComponentsID.UpNext,
            canShowUSQWarningCard() && RowComponentsID.USQ,
            ...itemList,
            this.getLoadMoreRowComponent(tableView), // 8. Spinner or Load Error
        ].filter(Boolean);
    };

    private getLoadMoreRowComponent = (tableView: TableView) =>
        this.hasMoreItemsOnServer() &&
        (tableView.hasErrorLoadingTable ? RowComponentsID.LoadError : RowComponentsID.Spinner);

    private doesTableConversationExistForItem = (itemId: string): boolean => {
        const tableView = getSelectedTableView();
        return shouldShowMailListItem(itemId, tableView);
    };

    private getItemHeight = (index: number): number => this.itemHeightsInPx[index];

    /**
     * Add SelectedTableView Id also while creating React Key, since it might be possible
     * that two folder have same conversationId in their respective store which might
     * leads to UI inconsistency while switching from one folder to another.
     */
    private getItemReactKey = (index: number, itemData: string[]): string =>
        itemData[index] + getSelectedTableView().id;

    private hasMoreItemsOnServer = (): boolean => {
        const tableView = getSelectedTableView();
        return getCanTableLoadMore(tableView);
    };

    private getIsItemLoaded = (index: number): boolean => {
        const virtualListItemIds = this.virtualListItemIds;
        // Every item is loaded except for our loading spinner row.
        return (
            index < virtualListItemIds.length &&
            virtualListItemIds[index] !== RowComponentsID.Spinner
        );
    };

    private fetchMoreItems = (): Promise<void> => {
        // If VML is being re-used, such as in case of UnifiedSearch, allow callback from props to be executed
        if (this.props.onLoadMoreItems) {
            return this.props.onLoadMoreItems();
        }
        const tableView = getSelectedTableView();
        if (tableView.hasErrorLoadingTable) {
            return Promise.resolve();
        }
        return loadMoreInTable(tableView);
    };

    private renderMailItem = (props: ItemRendererProp<string>): JSX.Element => (
        <VirtualizedMailListItem {...props} forwardedRefs={this.forwardedRefs} />
    );

    private isDataSourceSwitching(): boolean {
        // true when we are switching a folder or filter, (data-source is changing)
        return this.lastDataSourceId !== getSelectedTableView().id;
    }

    private async onRefreshListRequest() {
        const tableView = getSelectedTableView();

        const datapoint = new PerformanceDatapoint('PullToRefresh', {
            timeout: DATAPOINT_TIMEOUT,
            isCore: true,
        });

        try {
            await reloadTable(tableView);
            datapoint.end();
        } catch (error) {
            datapoint.endWithError(getDatapointStatus(error.message, error).status, error);
            throw error;
        }
    }

    render() {
        this.virtualListItemIds = this.getVirtualListItemIds();
        this.itemHeightsInPx = this.virtualListItemIds.map(virtualListItemId =>
            getListItemHeight(virtualListItemId, this.context)
        );
        const isInCheckedMode = getIsInCheckedMode();

        return (
            <VirtualizedList
                isRTL={getRTL()}
                enableIsScrolling={true}
                shouldScrollToTop={this.isDataSourceSwitching()}
                shouldScrollToBottom={this.virtualListItemIds.includes(RowComponentsID.LoadError)}
                itemData={this.virtualListItemIds}
                containerHeight={getMailListContainerHeight}
                itemReactKey={this.getItemReactKey}
                itemHeight={this.getItemHeight}
                getIsItemLoaded={this.getIsItemLoaded}
                fetchMoreItems={this.fetchMoreItems}
                itemCount={this.virtualListItemIds.length}
                itemRenderer={this.renderMailItem}
                onRefreshListRequest={this.onRefreshListRequest}
                className={classNames({
                    [styles.iOSSafeArea]: isIOS() && isInSearchView() && !isInCheckedMode,
                    [styles.unifiedSearchView]: isUnifiedSearchView() && !isInCheckedMode,
                })}
                enableSwipeRefresh={this.props.enableSwipeRefresh}
            />
        );
    }
}
